import { POST, PUT, SUCCESS } from "../utils/const/http-const";
import { getHeaders } from "./utils";

const BASE = "https://gestiondeflota.boltrack.net/apiultimamillav3/api/viaje-orden-kanban";

/**
 * Crea un nuevo orden.
 * Obligatorios en obj: idEmpresa, placa, secuencia.
 * @returns {Promise<{error: boolean, id?: string, message?: string}>}
 */
export async function crearViajeOrdenKanban(obj) {
    try {
        const response = { error: true };
        const { code, body, message } = await fetch(
            `${BASE}/crear-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            response.error = false;
            response.id = body?.id ?? null;
            response.message = message ?? null;
            return response;
        }
        response.message = message ?? "No se pudo crear el orden";
        return response;
    } catch (err) {
        console.log("crearViajeOrdenKanban err");
        console.log(err);
        return { error: true, message: err?.message ?? String(err) };
    }
}

/**
 * Lista órdenes con filtros opcionales.
 * @returns {Promise<Array>} Array (posiblemente vacío) — siempre devuelve array para consumo directo.
 */
export async function obtenerListaViajeOrdenKanban(obj = {}) {
    try {
        const { code, body } = await fetch(
            `${BASE}/obtener-lista-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS && Array.isArray(body)) {
            return body;
        }
        return [];
    } catch (err) {
        console.log("obtenerListaViajeOrdenKanban err");
        console.log(err);
        return [];
    }
}

/**
 * Trae un orden por id.
 * @returns {Promise<Object|null>} objeto orden o null si no existe / error.
 */
export async function obtenerViajeOrdenKanban(obj) {
    try {
        const { code, body } = await fetch(
            `${BASE}/obtener-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS && body != null) {
            return body;
        }
        return null;
    } catch (err) {
        console.log("obtenerViajeOrdenKanban err");
        console.log(err);
        return null;
    }
}

/**
 * Trae el orden vigente (habilitado=1 y estado ∈ borrador/confirmado/en_curso) de una placa.
 * @returns {Promise<Object|null>} objeto orden o null si no hay vigente.
 */
export async function obtenerVigentePorPlaca(obj) {
    try {
        const { code, body } = await fetch(
            `${BASE}/obtener-vigente-por-placa`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            // body puede ser null cuando no hay vigente — es respuesta válida
            return body ?? null;
        }
        return null;
    } catch (err) {
        console.log("obtenerVigentePorPlaca err");
        console.log(err);
        return null;
    }
}

/**
 * Actualiza el orden completo.
 * Obligatorios en obj: id, secuencia.
 * @returns {Promise<{error: boolean, message?: string}>}
 */
export async function actualizarViajeOrdenKanban(obj) {
    try {
        const response = { error: true };
        const { code, message } = await fetch(
            `${BASE}/actualizar-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            response.error = false;
            response.message = message ?? null;
            return response;
        }
        response.message = message ?? "No se pudo actualizar el orden";
        return response;
    } catch (err) {
        console.log("actualizarViajeOrdenKanban err");
        console.log(err);
        return { error: true, message: err?.message ?? String(err) };
    }
}

/**
 * Actualización parcial: solo secuencia + totales + flag manual. Ideal para drag-and-drop.
 * Obligatorios en obj: id, secuencia.
 * @returns {Promise<{error: boolean, message?: string}>}
 */
export async function actualizarSecuenciaViajeOrdenKanban(obj) {
    try {
        const response = { error: true };
        const { code, message } = await fetch(
            `${BASE}/actualizar-secuencia-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            response.error = false;
            response.message = message ?? null;
            return response;
        }
        response.message = message ?? "No se pudo actualizar la secuencia";
        return response;
    } catch (err) {
        console.log("actualizarSecuenciaViajeOrdenKanban err");
        console.log(err);
        return { error: true, message: err?.message ?? String(err) };
    }
}

/**
 * Cambia el estado del orden.
 * Obligatorios en obj: id, estado.
 * estado ∈ ('borrador','confirmado','en_curso','completado','cancelado').
 * Enviar `forzar: true` en obj para saltar el bloqueo de estados terminales.
 * @returns {Promise<{error: boolean, message?: string}>}
 */
export async function actualizarEstadoViajeOrdenKanban(obj) {
    try {
        const response = { error: true };
        const { code, message } = await fetch(
            `${BASE}/actualizar-estado-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            response.error = false;
            response.message = message ?? null;
            return response;
        }
        response.message = message ?? "No se pudo cambiar el estado del orden";
        return response;
    } catch (err) {
        console.log("actualizarEstadoViajeOrdenKanban err");
        console.log(err);
        return { error: true, message: err?.message ?? String(err) };
    }
}

/**
 * Soft delete (habilitado = 0).
 * Obligatorios en obj: id.
 * @returns {Promise<{error: boolean, message?: string}>}
 */
export async function deshabilitarViajeOrdenKanban(obj) {
    try {
        const response = { error: true };
        const { code, message } = await fetch(
            `${BASE}/deshabilitar-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            response.error = false;
            response.message = message ?? null;
            return response;
        }
        response.message = message ?? "No se pudo deshabilitar el orden";
        return response;
    } catch (err) {
        console.log("deshabilitarViajeOrdenKanban err");
        console.log(err);
        return { error: true, message: err?.message ?? String(err) };
    }
}

/**
 * Reactiva un registro deshabilitado (habilitado = 1).
 * Obligatorios en obj: id.
 * @returns {Promise<{error: boolean, message?: string}>}
 */
export async function habilitarViajeOrdenKanban(obj) {
    try {
        const response = { error: true };
        const { code, message } = await fetch(
            `${BASE}/habilitar-viaje-orden-kanban`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());
        if (code === SUCCESS) {
            response.error = false;
            response.message = message ?? null;
            return response;
        }
        response.message = message ?? "No se pudo habilitar el orden";
        return response;
    } catch (err) {
        console.log("habilitarViajeOrdenKanban err");
        console.log(err);
        return { error: true, message: err?.message ?? String(err) };
    }
}
